import React,{useEffect,useMemo,useState}from"react";
import{Box,Paper,Typography,TextField,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TablePagination,InputAdornment,Button,Dialog,CircularProgress,useMediaQuery}from"@mui/material";
import SearchRoundedIcon from"@mui/icons-material/SearchRounded";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAlert}from"../../context/AlertContext";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import PrintInvoice from"./AdminInvoice/PrintInvoice";

function formatMoney(value){
  const number=Number(value||0);
  return number.toLocaleString("es-EC",{minimumFractionDigits:2,maximumFractionDigits:2});
}

function getDocumentCode(factura){
  return factura.numero_documento||factura.numero||"-";
}

function getPersonaName(factura){
  return factura.persona?.nombre_comercial||factura.persona?.razon_social||"Consumidor Final";
}

function getPersonaType(factura){
  const tipo=factura.persona?.tipo||factura.persona?.tipo_persona||factura.persona?.rol||"";
  if(String(tipo).toLowerCase().includes("proveedor"))return"Proveedor";
  return"Cliente";
}

function getEstadoSri(factura){
  const estado=factura.sri?.estado||factura.sri_estado||factura.estado||"";
  if(estado==="AUTORIZADO")return"Autorizada";
  if(estado==="AUTORIZADA")return"Autorizada";
  if(estado==="NO_AUTORIZADO")return"No autorizada";
  if(estado==="NO_AUTORIZADA")return"No autorizada";
  if(estado==="ANULADA")return"Anulada";
  if(estado==="ERROR")return"Error";
  return estado||"-";
}

function getEstadoRaw(factura){
  return String(factura.sri?.estado||factura.sri_estado||factura.estado||"").toUpperCase();
}

function getStatusStyles(theme,estado){
  const raw=String(estado||"").toUpperCase();
  if(raw.includes("AUTORIZ"))return{bg:"#237A36",color:"#FFFFFF",border:"#237A36"};
  if(raw.includes("NO")||raw.includes("ERROR"))return{bg:"#B91C1C",color:"#FFFFFF",border:"#B91C1C"};
  if(raw.includes("ANUL"))return{bg:"#92400E",color:"#FFFFFF",border:"#92400E"};
  return{bg:theme.theme_mode==="dark"?"rgba(255,255,255,0.08)":"#E5E7EB",color:theme.text_color,border:theme.border_color};
}

function EstadoBadge({factura,theme}){
  const estado=getEstadoSri(factura);
  const styles=getStatusStyles(theme,estado);
  return(
    <Box component="span" sx={{display:"inline-flex",alignItems:"center",px:1.15,py:.5,borderRadius:"8px",bgcolor:styles.bg,color:styles.color,border:`1px solid ${styles.border}`,fontSize:12,fontWeight:700,lineHeight:1,whiteSpace:"nowrap"}}>
      {estado}
    </Box>
  );
}

export default function AdminDocuments(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showError}=useAlert();
  const isMobile=useMediaQuery("(max-width:900px)");
  const[documents,setDocuments]=useState([]);
  const[search,setSearch]=useState("");
  const[desde,setDesde]=useState("");
  const[hasta,setHasta]=useState("");
  const[loading,setLoading]=useState(false);
  const[downloadingId,setDownloadingId]=useState(null);
  const[page,setPage]=useState(0);
  const rowsPerPage=isMobile?10:30;
  const token=localStorage.getItem("accessToken");
  const downloading=Boolean(downloadingId);

  const authFetch=(url,options={})=>fetch(url,{
    ...options,
    headers:{
      ...(options.headers||{}),
      Authorization:`Bearer ${token}`
    }
  });

  const loadDocuments=async()=>{
    try{
      setLoading(true);
      const params=new URLSearchParams();
      params.append("page","1");
      params.append("page_size","100");
      if(search.trim())params.append("search",search.trim());
      if(desde)params.append("desde",desde);
      if(hasta)params.append("hasta",hasta);
      const response=await fetch(`/api/facturacion/documentos/facturas/?${params.toString()}`,{headers:{Authorization:`Bearer ${token}`}});
      const data=await response.json();
      if(!response.ok){
        showError(typeof(data.error||data.detail)==="string"?(data.error||data.detail):"No se pudieron cargar los documentos");
        return;
      }
      setDocuments(data.results||[]);
    }catch{
      showError("Error de conexión");
    }finally{
      setLoading(false);
    }
  };

  const downloadPdf=async documento=>{
    if(downloading)return;
    try{
      const pdfUrl=documento.archivos?.pdf_generado||`/api/facturacion/facturas/${documento.id}/pdf/`;
      if(!documento.id){
        showError("No se pudo descargar el PDF porque el documento no tiene ID.");
        return;
      }
      setDownloadingId(documento.id);
      const response=await fetch(pdfUrl,{method:"GET",headers:{Authorization:`Bearer ${token}`}});
      if(!response.ok){
        const text=await response.text();
        showError(text||"No se pudo descargar el PDF");
        return;
      }
      const blob=await response.blob();
      const url=window.URL.createObjectURL(blob);
      const link=window.document.createElement("a");
      const numero=getDocumentCode(documento).replaceAll("/","-").replaceAll("\\","-");
      link.href=url;
      link.download=`Factura_${numero}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }catch{
      showError("Error de conexión al descargar el PDF");
    }finally{
      setDownloadingId(null);
    }
  };

  useEffect(()=>{
    const delay=setTimeout(()=>{
      setPage(0);
      loadDocuments();
    },350);
    return()=>clearTimeout(delay);
  },[search,desde,hasta]);

  useEffect(()=>{
    setPage(0);
  },[rowsPerPage]);

  const filteredDocuments=useMemo(()=>{
    return documents.filter(documento=>{
      const estado=getEstadoRaw(documento);
      return!estado.includes("ANUL");
    });
  },[documents]);

  const visibleDocuments=useMemo(()=>{
    const start=page*rowsPerPage;
    return filteredDocuments.slice(start,start+rowsPerPage);
  },[filteredDocuments,page,rowsPerPage]);

  const handleChangePage=(_event,newPage)=>{
    if(downloading)return;
    setPage(newPage);
  };

  const smallButtonSx={
    minHeight:34,
    px:1.7,
    borderRadius:"10px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    whiteSpace:"nowrap",
    "&:hover":{borderColor:dashboardTheme.button_color,bgcolor:`${dashboardTheme.button_color}12`,boxShadow:"none"},
    "&.Mui-disabled":{color:dashboardTheme.muted_text_color,borderColor:dashboardTheme.border_color}
  };

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2,mb:{xs:1.5,md:2}}}>Documentos</Typography>
            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={{...dashboardTheme.tableStyles.toolbar,display:"flex",flexDirection:"column",alignItems:"stretch",gap:2}}>
                <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},flexDirection:{xs:"column",sm:"row"},gap:{xs:1.3,sm:2}}}>
                  <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},flexDirection:{xs:"column",sm:"row"},gap:1.2}}>
                    <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.text_color,whiteSpace:"nowrap"}}>Desde</Typography>
                    <TextField size="small" type="date" value={desde} disabled={downloading} onChange={event=>setDesde(event.target.value)} sx={{minWidth:{xs:"100%",sm:210},...dashboardTheme.inputStyles}}/>
                  </Box>
                  <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},flexDirection:{xs:"column",sm:"row"},gap:1.2}}>
                    <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.text_color,whiteSpace:"nowrap"}}>Hasta</Typography>
                    <TextField size="small" type="date" value={hasta} disabled={downloading} onChange={event=>setHasta(event.target.value)} sx={{minWidth:{xs:"100%",sm:210},...dashboardTheme.inputStyles}}/>
                  </Box>
                </Box>
                <TextField fullWidth size="small" placeholder="Buscar por código, número de documento o persona" value={search} disabled={downloading} onChange={event=>setSearch(event.target.value)} InputProps={{startAdornment:<InputAdornment position="start"><SearchRoundedIcon sx={{color:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#6b7280"}}/></InputAdornment>}} sx={{...dashboardTheme.inputStyles,"& .MuiInputBase-input::placeholder":{color:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.55)":"#6b7280",opacity:1}}}/>
              </Box>
              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:120}}>Emisión</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:300}}>Persona</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:230}}>Documento</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:190}}>Estado / Autoriz. SRI</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Neto</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Imp.</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Total</TableCell>
                        <TableCell align="right" sx={{...dashboardTheme.tableStyles.headCell,minWidth:290}}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleDocuments.map(documento=>{
                        const documentCode=getDocumentCode(documento);
                        const persona=getPersonaName(documento);
                        const personaTipo=getPersonaType(documento);
                        const neto=Number(documento.subtotal||documento.total_sin_impuestos||0);
                        const impuesto=Number(documento.iva||documento.total_iva||0);
                        const total=Number(documento.total||0);
                        const isDownloading=downloadingId===documento.id;
                        return(
                          <TableRow key={documento.id} hover sx={dashboardTheme.tableStyles.bodyRow}>
                            <TableCell sx={{color:dashboardTheme.text_color,fontWeight:700,whiteSpace:"normal",lineHeight:1.25}}>{documento.fecha_emision||"-"}</TableCell>
                            <TableCell>
                              <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.text_color,lineHeight:1.25,textTransform:"uppercase"}}>{persona}</Typography>
                              <Typography sx={{mt:.45,fontSize:12.5,fontWeight:500,color:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#64748B",lineHeight:1.25}}>{personaTipo}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.button_color,lineHeight:1.25}}>{documentCode}</Typography>
                            </TableCell>
                            <TableCell><EstadoBadge factura={documento} theme={dashboardTheme}/></TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap",color:dashboardTheme.text_color,fontWeight:700}}>${formatMoney(neto)}</TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap",color:dashboardTheme.text_color,fontWeight:700}}>${formatMoney(impuesto)}</TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap",color:dashboardTheme.text_color,fontWeight:700}}>${formatMoney(total)}</TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                              <Box sx={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:1}}>
                                <PrintInvoice factura={documento} authFetch={authFetch} disabled={downloading}/>
                                <Button variant="outlined" size="small" disabled={downloading} onClick={()=>downloadPdf(documento)} sx={smallButtonSx}>{isDownloading?"Descargando":"Descargar"}</Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {!loading&&visibleDocuments.length===0&&(
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={dashboardTheme.tableStyles.emptyCell}>No hay documentos registrados</TableCell>
                        </TableRow>
                      )}
                      {loading&&(
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={dashboardTheme.tableStyles.emptyCell}>Cargando documentos...</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <TablePagination component="div" count={filteredDocuments.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} rowsPerPageOptions={[]} labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`} sx={dashboardTheme.tableStyles.pagination}/>
            </Paper>
          </Box>
        </Box>
      </Box>
      <Dialog open={downloading} disableEscapeKeyDown PaperProps={{sx:{width:"min(360px, calc(100vw - 32px))",borderRadius:"20px",bgcolor:dashboardTheme.card_background,border:`1px solid ${dashboardTheme.border_color}`,boxShadow:"none",p:3,textAlign:"center"}}} BackdropProps={{sx:{bgcolor:dashboardTheme.theme_mode==="dark"?"rgba(0,0,0,0.72)":"rgba(15,23,42,0.35)"}}}>
        <Box sx={{width:58,height:58,borderRadius:"50%",mx:"auto",mb:2,display:"flex",alignItems:"center",justifyContent:"center",bgcolor:`${dashboardTheme.primary_color}16`}}>
          <CircularProgress size={30} thickness={4} sx={{color:dashboardTheme.primary_color}}/>
        </Box>
        <Typography sx={{fontSize:20,fontWeight:700,color:dashboardTheme.text_color,lineHeight:1.2,mb:.8}}>Descargando</Typography>
        <Typography sx={{fontSize:14,fontWeight:500,color:dashboardTheme.muted_text_color,lineHeight:1.5}}>Espere mientras se genera el archivo.</Typography>
      </Dialog>
    </Box>
  );
}